import { useCallback, useEffect, useRef, useState } from 'react';

// audio/mp4 first: the only format validated against Mistral so far (21/10). Browsers that
// can't record mp4 (Firefox, older Chrome / Android) fall back to webm.
const PREFERRED_MIME_TYPES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];

export interface VoiceRecording {
  audio: Blob;
  filename: string;
  durationSeconds: number;
}

const pickMimeType = (): string | undefined =>
  PREFERRED_MIME_TYPES.find(mime => MediaRecorder.isTypeSupported(mime));

const extensionFor = (mime: string): string => (mime.startsWith('audio/mp4') ? 'm4a' : 'webm');

export const isVoiceRecordingSupported = (): boolean =>
  typeof window !== 'undefined' &&
  typeof MediaRecorder !== 'undefined' &&
  Boolean(navigator.mediaDevices?.getUserMedia);

/**
 * Records the microphone until `stop()` is called or `maxDurationSeconds` is reached, then hands
 * the audio to `onRecorded`. The audio only lives in memory: nothing is stored on the device.
 */
export const useVoiceRecorder = ({
  maxDurationSeconds,
  onRecorded,
}: {
  maxDurationSeconds: number;
  onRecorded: (recording: VoiceRecording) => void;
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<number>();
  const onRecordedRef = useRef(onRecorded);
  onRecordedRef.current = onRecorded;

  const stop = useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  // Throws the browser's error (e.g. NotAllowedError when the microphone is refused).
  const start = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const chunks: Blob[] = [];
    const startedAt = Date.now();

    recorder.ondataavailable = event => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };
    recorder.onstop = () => {
      window.clearInterval(timerRef.current);
      stream.getTracks().forEach(track => track.stop());
      recorderRef.current = null;
      setIsRecording(false);
      // The API validates the base mime type: drop the ";codecs=…" suffix.
      const type = (recorder.mimeType || mimeType || 'audio/webm').split(';')[0];
      onRecordedRef.current({
        audio: new Blob(chunks, { type }),
        filename: `dictation.${extensionFor(type)}`,
        durationSeconds: Math.round((Date.now() - startedAt) / 1000),
      });
    };

    recorderRef.current = recorder;
    recorder.start();
    setElapsedSeconds(0);
    setIsRecording(true);
    timerRef.current = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setElapsedSeconds(elapsed);
      if (elapsed >= maxDurationSeconds) {
        stop();
      }
    }, 250);
  }, [maxDurationSeconds, stop]);

  // Closing the chat mid-dictation releases the microphone and drops the audio.
  useEffect(
    () => () => {
      window.clearInterval(timerRef.current);
      const recorder = recorderRef.current;
      if (recorder) {
        recorder.onstop = null;
        recorder.stream.getTracks().forEach(track => track.stop());
        if (recorder.state === 'recording') {
          recorder.stop();
        }
      }
    },
    []
  );

  return { isRecording, elapsedSeconds, start, stop };
};
